"use client";

import { useAuthStore } from "@/store/auth-store";
import { LogOut, Moon, Sun, ChevronRight } from "lucide-react";
import { useTheme } from "next-themes";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";

/** SSR 안전한 mount 감지 — next-themes hydration mismatch 회피 */
function useMounted(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
}

const routeLabels: Record<string, string> = {
  "": "대시보드",
  users: "사용자 관리",
  wallets: "지갑 관리",
  "security-tokens": "토큰증권(STO)",
  "public-offerings": "공모 관리",
  // 계좌관리기관
  "account-manager": "계좌관리기관",
  "issue-token": "토큰 발행",
  "subscriptions": "청약 관리",
  "customer-accounts": "고객계좌부",
  "deposits": "예치금 관리",
  "issuer-accounts": "발행인관리계좌부",
  "own-accounts": "자기계좌부",
  "rights": "권리행사 일정",
  "beneficiary-register": "권리자명부",
  "reconciliation": "KSD 대조",
  "compliance": "운영 컴플라이언스",
  "reports": "감독기관 보고",
  // 동향
  "sto-news": "STO 동향",
  "regulatory-tracker": "시행령 추적",
  "regulations": "관련 법령",
  "resources": "교육 자료",
  // 공통
  create: "새로 만들기",
};

function Breadcrumbs() {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return (
      <div className="flex items-center gap-1 text-sm">
        <span className="font-medium text-gray-900 dark:text-gray-100">대시보드</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-sm">
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        const label = routeLabels[seg] || (seg.length === 24 ? `#${seg.slice(0, 8)}` : seg);
        return (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3 text-gray-300 dark:text-gray-600" />}
            <span
              className={cn(
                isLast
                  ? "font-medium text-gray-900 dark:text-gray-100"
                  : "text-gray-400 dark:text-gray-500"
              )}
            >
              {label}
            </span>
          </span>
        );
      })}
    </div>
  );
}

const roleLabels: Record<string, string> = {
  ADMIN: "관리자",
  ISSUER: "발행인",
  INVESTOR: "투자자",
  OPERATOR: "운영자",
};

const roleColors: Record<string, string> = {
  ADMIN: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
  ISSUER: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  INVESTOR: "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300",
  OPERATOR: "bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
};

export function Header() {
  const { role, user, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const mounted = useMounted();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClick = () => setShowProfileMenu(false);
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [showProfileMenu]);

  const handleLogout = () => {
    logout();
    router.push("/login");
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-gray-200/80 bg-white/80 px-6 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950/80">
      {/* Left: Breadcrumbs */}
      <Breadcrumbs />

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        {mounted && (
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title={theme === "dark" ? "라이트 모드" : "다크 모드"}
          >
            {theme === "dark" ? (
              <Sun className="h-[18px] w-[18px]" />
            ) : (
              <Moon className="h-[18px] w-[18px]" />
            )}
          </button>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />

        {/* User Profile */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowProfileMenu(!showProfileMenu);
            }}
            className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-xs font-bold text-white">
              {(user?.name || "A").charAt(0)}
            </div>
            <div className="hidden text-left sm:block">
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                {user?.name || "관리자"}
              </p>
              <p className="text-[10px] text-gray-400">{roleLabels[role] || role}</p>
            </div>
          </button>

          {/* Profile Dropdown */}
          {showProfileMenu && (
            <div className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
              <div className="border-b border-gray-100 p-3 dark:border-gray-800">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {user?.name || "관리자"}
                </p>
                <p className="text-xs text-gray-500">{user?.email || ""}</p>
                <span
                  className={cn(
                    "mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                    roleColors[role] || "bg-gray-100 text-gray-700"
                  )}
                >
                  {roleLabels[role] || role}
                </span>
              </div>
              <div className="p-1">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                >
                  <LogOut className="h-4 w-4" />
                  로그아웃
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
